import React, { useMemo, useState, useCallback } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import type { Dayjs } from 'dayjs';
import apiClient from '../../../utils/axiosClient';
import { useDateSort } from '../../../hooks/useDateSort';
import TableActionsMenu from '../../common/TableActionsMenu';
import type { SettlementStatusRow } from '../../../types/pointhomeSettlementStatus.types';

type Props = {
  data: SettlementStatusRow[];
  startDate: Dayjs;
  endDate: Dayjs;
  selectedApp: string | null;
  selectedOs: string;
};

const fmtWon = (v: number) => `₩${Math.round(v ?? 0).toLocaleString('ko-KR')}`;
const fmtNum = (v: number) => Math.round(v ?? 0).toLocaleString('ko-KR');

const CASH_KEYS = new Set<keyof SettlementStatusRow>([
  'dau',
  'accumulatedCash',
  'usedCash',
  'ticketReceiveCount',
]);
const fmt = (key: keyof SettlementStatusRow, v: number) =>
  CASH_KEYS.has(key) ? fmtNum(v) : fmtWon(v);

// 그룹 없는 기본 컬럼 (헤더 row 1에서 rowSpan=2)
const BASE_COLUMNS: { key: keyof SettlementStatusRow; label: string }[] = [
  { key: 'date', label: '날짜' },
  { key: 'dau', label: 'DAU' },
  { key: 'totalRevenue', label: '총 매출' },
  { key: 'expectedRevenue', label: '예상 수익' },
  { key: 'commission', label: '수수료' },
  { key: 'ticketReceiveCount', label: '티켓 발급 수' },
  { key: 'expense', label: '비용' },
  { key: 'expenseSSPCommission', label: 'SSP 수수료' },
  { key: 'expenseGifticon', label: '기프티콘' },
  { key: 'expenseBankTransfer', label: '계좌이체' },
  { key: 'expenseBankTransferCommission', label: '이체 수수료' },
  { key: 'expenseBankAccountRealName', label: '계좌 실명확인' },
  { key: 'expenseCashExchange', label: '캐시전환' },
  { key: 'expenseNpayExchange', label: 'N페이 전환' },
  { key: 'accumulatedCash', label: '누적 캐시' },
  { key: 'usedCash', label: '사용 캐시' },
];

type SubColumn = { key: keyof SettlementStatusRow; label: string };
type ColumnGroup = {
  groupLabel: string;
  subtotalLabel: string;
  headerBg: string;
  subBg: string;
  subColumns: SubColumn[];
};

const COLUMN_GROUPS: ColumnGroup[] = [
  {
    groupLabel: 'SSP',
    subtotalLabel: 'SSP 합계',
    headerBg: '#dbeafe',
    subBg: '#eff6ff',
    subColumns: [
      { key: 'revenueSSPBannerAttendance', label: '출석체크' },
      { key: 'revenueSSPBannerRedeem', label: '황금티켓' },
      { key: 'revenueSSPBannerTicketRoulette', label: '티켓얻기' },
      { key: 'revenueSSPBannerOthers', label: '기타' },
      { key: 'revenueSSPBannerMoneyArcade', label: '머니오락실' },
      { key: 'revenueSSPBannerPointhomeCleaning', label: '해달 청소하기' },
      { key: 'revenueSSPBannerPointhomeDishing', label: '해달 설거지하기' },
    ],
  },
  {
    groupLabel: '베네핏',
    subtotalLabel: '베네핏 합계',
    headerBg: '#bbf7d0',
    subBg: '#dcfce7',
    subColumns: [{ key: 'revenueBenefitCPC', label: '베네핏 CPC' }],
  },
  {
    groupLabel: 'CPC',
    subtotalLabel: 'CPC 합계',
    headerBg: '#86efac',
    subBg: '#bbf7d0',
    subColumns: [
      { key: 'revenueMindSoftLabCPC', label: '마인드소프트랩' },
      { key: 'revenueQuantumbitCPC', label: '퀀텀비트' },
      { key: 'revenueCaulyCPC', label: '카울리' },
      { key: 'revenueCoupangCPC', label: '쿠팡' },
    ],
  },
  {
    groupLabel: 'RC',
    subtotalLabel: 'RC 합계',
    headerBg: '#fed7aa',
    subBg: '#fff7ed',
    subColumns: [
      { key: 'revenueRCNews', label: '뉴스' },
      { key: 'revenueRcOneLineNews', label: '한줄뉴스' },
      { key: 'revenueSSPBannerFishingRoulette', label: '낚시룰렛' },
      { key: 'revenueRcRoulette', label: '룰렛 더 모으기' },
      { key: 'revenueRcContentsArcade', label: '콘텐츠오락실' },
    ],
  },
  {
    groupLabel: '기타',
    subtotalLabel: '기타 합계',
    headerBg: '#e9d5ff',
    subBg: '#faf5ff',
    subColumns: [
      { key: 'revenueBuzzADSS2SIntegrationUnits', label: 'BuzzAD S2S' },
      { key: 'revenueNaverDirect', label: '네이버 직연동' },
      { key: 'revenueMagazineDirect', label: '매거진 직연동' },
      { key: 'revenueWereview', label: '위리뷰' },
      { key: 'revenueHaruStory', label: '하루스토리' },
      { key: 'revenueCoupangSeller', label: '쿠팡 셀러' },
    ],
  },
  {
    groupLabel: '오퍼월',
    subtotalLabel: '오퍼월 합계',
    headerBg: '#fecdd3',
    subBg: '#fff1f2',
    subColumns: [
      { key: 'revenueAdforusOfferwall', label: '애드포러스' },
      { key: 'revenueBuzzvilOfferwall', label: '버즈빌' },
      { key: 'revenueFlexOfferwall', label: '플렉스' },
      { key: 'revenueGPAOfferwall', label: 'GPA' },
      { key: 'revenueIGAWorksOfferwall', label: 'IGA웍스' },
      { key: 'revenueIVEOfferwall', label: 'IVE' },
      { key: 'revenueKeywordlabOfferwall', label: '키워드랩' },
      { key: 'revenueMindSoftLabOfferwall', label: '마인드소프트랩' },
      { key: 'revenueNASOfferwall', label: 'NAS' },
      { key: 'revenueNasmediaOfferwall', label: '나스미디어' },
      { key: 'revenueOhcOfferwall', label: 'OHC' },
      { key: 'revenuePincruxOfferwall', label: '핀크럭스' },
      { key: 'revenueQuantumbitOfferwall', label: '퀀텀비트' },
    ],
  },
];

const ROWS_PER_PAGE = 31;

function groupSubtotal(row: SettlementStatusRow, group: ColumnGroup): number {
  return group.subColumns.reduce((sum, col) => sum + ((row[col.key] as number) ?? 0), 0);
}

const cellSx = {
  fontSize: '0.8rem' as const,
  whiteSpace: 'nowrap' as const,
  py: 0.5,
  px: 1,
  border: '1px solid #f1f5f9',
};

const headSx = {
  ...cellSx,
  fontWeight: 600,
  py: 0.75,
  border: '1px solid #e2e8f0',
};

export default function PointhomeSettlementStatusTable({
  data,
  startDate,
  endDate,
  selectedApp,
  selectedOs,
}: Props) {
  const [page, setPage] = useState(0);
  const { sorted, sortDirection, toggleSort } = useDateSort(data);

  const baseTotals = useMemo(() => {
    const result: Record<string, number> = {};
    for (const col of BASE_COLUMNS.slice(1)) {
      result[col.key] = sorted.reduce((sum, row) => sum + ((row[col.key] as number) ?? 0), 0);
    }
    return result;
  }, [sorted]);

  const groupTotals = useMemo(
    () =>
      COLUMN_GROUPS.map((group) => ({
        subtotal: sorted.reduce((sum, row) => sum + groupSubtotal(row, group), 0),
        byCols: Object.fromEntries(
          group.subColumns.map((col) => [
            col.key,
            sorted.reduce((sum, row) => sum + ((row[col.key] as number) ?? 0), 0),
          ]),
        ),
      })),
    [sorted],
  );

  const paged = useMemo(
    () => sorted.slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE),
    [sorted, page],
  );

  const handleDownloadXlsx = useCallback(() => {
    const params: Record<string, string> = {
      start_date: startDate.format('YYYY-MM-DD'),
      end_date: endDate.format('YYYY-MM-DD'),
    };
    if (selectedApp && selectedApp !== '앱 전체') params.app_name = selectedApp;
    if (selectedOs && selectedOs !== 'all') params.os_type = selectedOs;

    apiClient
      .get('/api/v1/stat/pointhome/settlement/status/xlsx', { params, responseType: 'blob' })
      .then((res) => {
        const disposition = res.headers['content-disposition'] ?? '';
        const rfcMatch = disposition.match(/filename\*=UTF-8''([^;\n]+)/i);
        const plainMatch = disposition.match(/filename="?([^";\n]+)"?/i);
        const raw = rfcMatch?.[1] ?? plainMatch?.[1] ?? null;
        const filename = raw
          ? decodeURIComponent(raw.trim())
          : `통합지표_${startDate.format('YYMMDD')}_${endDate.format('YYMMDD')}.xlsx`;
        const url = URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
      });
  }, [startDate, endDate, selectedApp, selectedOs]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <TableActionsMenu onDownloadExcel={handleDownloadXlsx} />
      </Box>

      <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
        <Table size="small" sx={{ borderCollapse: 'collapse' }}>
          <TableHead>
            {/* ── 헤더 1행: 기본 컬럼(rowSpan=2) + 그룹 헤더(colSpan) ── */}
            <TableRow>
              {/* 날짜 */}
              <TableCell
                rowSpan={2}
                sx={{
                  ...headSx,
                  minWidth: 82,
                  verticalAlign: 'middle',
                  backgroundColor: '#f1f5f9',
                }}
              >
                <TableSortLabel active direction={sortDirection} onClick={toggleSort}>
                  날짜
                </TableSortLabel>
              </TableCell>
              {/* 나머지 기본 컬럼 */}
              {BASE_COLUMNS.slice(1).map((col) => (
                <TableCell
                  key={col.key}
                  rowSpan={2}
                  align="right"
                  sx={{
                    ...headSx,
                    minWidth: 88,
                    verticalAlign: 'middle',
                    backgroundColor: '#f1f5f9',
                  }}
                >
                  {col.label}
                </TableCell>
              ))}
              {/* 그룹 헤더 */}
              {COLUMN_GROUPS.map((group) => (
                <TableCell
                  key={group.groupLabel}
                  align="center"
                  colSpan={group.subColumns.length + 1}
                  sx={{ ...headSx, backgroundColor: group.headerBg, borderBottom: 'none' }}
                >
                  {group.groupLabel}
                </TableCell>
              ))}
            </TableRow>

            {/* ── 헤더 2행: 그룹별 합계 + 세부 컬럼 ── */}
            <TableRow>
              {COLUMN_GROUPS.map((group) => (
                <React.Fragment key={group.groupLabel}>
                  {/* 그룹 합계 컬럼 */}
                  <TableCell
                    align="right"
                    sx={{
                      ...headSx,
                      backgroundColor: group.headerBg,
                      fontWeight: 700,
                      minWidth: 88,
                    }}
                  >
                    {group.subtotalLabel}
                  </TableCell>
                  {/* 세부 컬럼들 */}
                  {group.subColumns.map((col) => (
                    <TableCell
                      key={col.key}
                      align="right"
                      sx={{ ...headSx, backgroundColor: group.subBg, minWidth: 80 }}
                    >
                      {col.label}
                    </TableCell>
                  ))}
                </React.Fragment>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {/* 합계 행 */}
            <TableRow sx={{ backgroundColor: '#f0f4ff' }}>
              <TableCell sx={{ ...cellSx, fontWeight: 700 }}>합계</TableCell>
              {BASE_COLUMNS.slice(1).map((col) => (
                <TableCell key={col.key} align="right" sx={{ ...cellSx, fontWeight: 700 }}>
                  {fmt(col.key, baseTotals[col.key] ?? 0)}
                </TableCell>
              ))}
              {COLUMN_GROUPS.map((group, gi) => (
                <React.Fragment key={group.groupLabel}>
                  <TableCell
                    align="right"
                    sx={{ ...cellSx, fontWeight: 700, backgroundColor: group.headerBg }}
                  >
                    {fmtWon(groupTotals[gi].subtotal)}
                  </TableCell>
                  {group.subColumns.map((col) => (
                    <TableCell
                      key={col.key}
                      align="right"
                      sx={{ ...cellSx, fontWeight: 700, backgroundColor: group.subBg }}
                    >
                      {fmtWon(groupTotals[gi].byCols[col.key as string] ?? 0)}
                    </TableCell>
                  ))}
                </React.Fragment>
              ))}
            </TableRow>

            {/* 데이터 행 */}
            {paged.map((row) => (
              <TableRow key={row.date} hover>
                <TableCell sx={cellSx}>{row.date}</TableCell>
                {BASE_COLUMNS.slice(1).map((col) => (
                  <TableCell key={col.key} align="right" sx={cellSx}>
                    {fmt(col.key, row[col.key] as number)}
                  </TableCell>
                ))}
                {COLUMN_GROUPS.map((group) => (
                  <React.Fragment key={group.groupLabel}>
                    <TableCell align="right" sx={{ ...cellSx, backgroundColor: group.headerBg }}>
                      {fmtWon(groupSubtotal(row, group))}
                    </TableCell>
                    {group.subColumns.map((col) => (
                      <TableCell
                        key={col.key}
                        align="right"
                        sx={{ ...cellSx, backgroundColor: group.subBg }}
                      >
                        {fmtWon(row[col.key] as number)}
                      </TableCell>
                    ))}
                  </React.Fragment>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={sorted.length}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={ROWS_PER_PAGE}
        rowsPerPageOptions={[ROWS_PER_PAGE]}
      />
    </Box>
  );
}
